import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { usePricing } from '../hooks/usePricing';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import ProListingCard from '../components/ProListingCard';
import ProListingFilters from '../components/ProListingFilters';
import ServicesHowItWorks from '../components/ServicesHowItWorks';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Megaphone, SearchX, AlertCircle } from 'lucide-react';

const EMPTY_FILTERS = { category: '', municipality: '', rate_min: '', rate_max: '', verified_only: false, q: '' };

const hasActiveFilters = (f) => (
  Boolean(f.category) || Boolean(f.municipality) || Boolean(f.rate_min) ||
  Boolean(f.rate_max) || Boolean(f.verified_only) || Boolean((f.q || '').trim())
);

/**
 * Services Marketplace directory (Phase 1a). Public, but flag-gated: when
 * pricing.services_enabled is false it renders a bookmark-safe "coming soon" shell.
 */
const Services = ({ profile, setProfile }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pricing, loading: pricingLoading } = usePricing();
  const servicesEnabled = Boolean(pricing?.services_enabled);

  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  // Keep the latest filters in a ref so fetchListings stays stable (no fetch-per-keystroke).
  const filtersRef = useRef(filters);
  useEffect(() => { filtersRef.current = filters; }, [filters]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get('/api/service-categories');
      setCategories(res.data.categories || []);
    } catch (err) {
      setCategories([]);
    }
  }, []);

  // Monotonic sequence guard: with auto-apply filters, rapid changes can start overlapping
  // requests; only the LATEST call may commit state (prevents stale results landing last).
  const fetchSeqRef = useRef(0);
  const fetchListings = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setLoadError(false);
    try {
      const f = filtersRef.current;
      const params = {};
      if (f.category) params.category = f.category;
      if (f.municipality) params.municipality = f.municipality;
      if (f.rate_min) params.rate_min = f.rate_min;
      if (f.rate_max) params.rate_max = f.rate_max;
      if (f.verified_only) params.verified_only = true;
      if (f.q && f.q.trim()) params.q = f.q.trim();
      const res = await api.get('/api/pro-listings', { params });
      if (seq !== fetchSeqRef.current) return;
      setListings(res.data.listings || []);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      setListings([]);
      setLoadError(true);
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (servicesEnabled) {
      fetchCategories();
      fetchListings();
    }
  }, [servicesEnabled, fetchCategories, fetchListings]);

  // servicesEnabled kept in a ref so the filter effects below can read it without listing it
  // as a dependency (which would refire them on the pricing flag flip and double-fetch).
  const servicesEnabledRef = useRef(servicesEnabled);
  useEffect(() => { servicesEnabledRef.current = servicesEnabled; }, [servicesEnabled]);

  // Auto-apply discrete filters (select/checkbox) immediately on change — matches Jobs.js.
  const { category, municipality, verified_only: verifiedOnly } = filters;
  const didMountFilters = useRef(false);
  useEffect(() => {
    // Skip the mount pass (the initial fetch runs from the servicesEnabled effect above).
    if (!didMountFilters.current) { didMountFilters.current = true; return; }
    if (servicesEnabledRef.current) fetchListings();
  }, [category, municipality, verifiedOnly, fetchListings]);

  // Debounce the free-text + rate inputs (300ms) — mirrors the Jobs.js live-search pattern.
  const { q, rate_min: rateMin, rate_max: rateMax } = filters;
  const didMountDebounce = useRef(false);
  useEffect(() => {
    // Consume the mount-skip FIRST (matching the discrete-filter effect above) — checking the
    // flag first meant the mount pass never consumed it, so the user's FIRST text/rate change
    // was swallowed as the "mount skip" and never fetched.
    if (!didMountDebounce.current) { didMountDebounce.current = true; return undefined; }
    if (!servicesEnabledRef.current) return undefined;
    const handle = setTimeout(() => { fetchListings(); }, 300);
    return () => clearTimeout(handle);
  }, [q, rateMin, rateMax, fetchListings]);

  const clearFilters = () => setFilters(EMPTY_FILTERS);
  const filtersActive = hasActiveFilters(filters);

  // While pricing resolves, render a bare shell — otherwise flag-off visitors see (and can
  // interact with) the full directory chrome for a beat before the coming-soon swap.
  if (pricingLoading) {
    return (
      <div className="min-h-screen-safe pb-nav md:pb-0">
        <TopBar profile={profile} setProfile={setProfile} />
        <div className="flex justify-center py-24">
          <div className="spinner" role="status" aria-label={t('common.loading', 'Loading...')} />
        </div>
        <BottomNav profile={profile} />
      </div>
    );
  }

  if (!servicesEnabled) {
    return (
      <div className="min-h-screen-safe pb-nav md:pb-0">
        <TopBar profile={profile} setProfile={setProfile} />
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <h1 className="text-2xl sm:text-3xl font-bold mb-3">{t('services.comingSoonTitle')}</h1>
          <p className="text-muted-foreground">{t('services.comingSoonBody')}</p>
        </div>
        <BottomNav profile={profile} />
      </div>
    );
  }

  return (
    <div className="min-h-screen-safe pb-nav md:pb-0">
      <TopBar profile={profile} setProfile={setProfile} />
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold">{t('services.directoryTitle')}</h1>
            <p className="text-muted-foreground">{t('services.directorySubtitle')}</p>
          </div>
          <Button
            className="min-h-[44px]"
            onClick={() => navigate('/services/requests/new')}
          >
            <Megaphone size={16} className="mr-2" aria-hidden="true" />
            {t('services.reqPostCta')}
          </Button>
        </div>

        <ServicesHowItWorks />

        <ProListingFilters
          categories={categories}
          filters={filters}
          setFilters={setFilters}
          onClear={clearFilters}
          filtersActive={filtersActive}
        />

        {(pricingLoading || loading) ? (
          <div className="flex justify-center py-16">
            <div className="spinner" role="status" aria-label={t('common.loading', 'Loading...')} />
          </div>
        ) : loadError ? (
          <Card className="border-dashed mt-6">
            <div className="p-8 flex flex-col items-center text-center gap-3">
              <div className="rounded-full bg-muted p-3">
                <AlertCircle className="text-muted-foreground" size={24} aria-hidden="true" />
              </div>
              <p className="text-sm font-medium">{t('services.loadErrorTitle')}</p>
              <p className="text-sm text-muted-foreground max-w-md">{t('services.loadErrorBody')}</p>
              <Button variant="outline" onClick={fetchListings}>{t('services.retry')}</Button>
            </div>
          </Card>
        ) : listings.length === 0 ? (
          <Card className="border-dashed mt-6">
            <div className="p-8 flex flex-col items-center text-center gap-3">
              <div className="rounded-full bg-muted p-3">
                <SearchX className="text-muted-foreground" size={24} aria-hidden="true" />
              </div>
              <p className="text-sm font-medium">
                {filtersActive ? t('services.empty') : t('services.emptyNoFilters')}
              </p>
              <div className="flex flex-wrap justify-center gap-2 pt-1">
                {filtersActive && (
                  <Button variant="outline" onClick={clearFilters}>{t('services.clearFilters')}</Button>
                )}
                <Button onClick={() => navigate('/services/requests/new')}>
                  <Megaphone size={16} className="mr-2" aria-hidden="true" />
                  {t('services.reqEscapeHatch')}
                </Button>
              </div>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {listings.map((l) => <ProListingCard key={l.id} listing={l} />)}
          </div>
        )}
      </div>
      <BottomNav profile={profile} />
    </div>
  );
};

export default Services;
